import { useContext, useMemo } from "react"
import Budget2023Context from "context/budget2023/Budget2023Context"
import AuthContext from "context/auth/authContext"
import useBudgetConfig, {
  BUDGET_CONFIG_TYPES,
} from "hooks/budgeting/useBudgetConfig"
import { useDecimalFormatter } from "hooks/useDecimalFormatter"
import { TOTALS } from "constants/decimals"
import { currencyFormatDE } from "lib/transformers"
import { getCurrency } from "utils/functions"

/**
 * Precio cliente total mostrado en el footer del presupuesto:
 * - licitación: coste reducido total + márgenes del modal (mismo criterio que BugdetStructure)
 * - estándar: totales del árbol según preferencia de descuento
 */
export function useBudgetClientPriceTotal() {
  const { budgetTree, budgetMode, marginModal } = useContext(Budget2023Context)
  const { user } = useContext(AuthContext)
  const [budgetConfig] = useBudgetConfig()
  const preferencias = budgetConfig[BUDGET_CONFIG_TYPES.PREFERENCES]
  const { getDecimals, getUnitWithMargin, roundToDecimals } =
    useDecimalFormatter()

  const clientPriceTotalAmount = useMemo(() => {
    if (budgetMode === "tender") {
      const margenTotal =
        marginModal.generalExpenses +
        marginModal.indirectExpenses +
        marginModal.industrialProfit
      return roundToDecimals(
        getUnitWithMargin(budgetTree?.priceReduced, margenTotal),
        getDecimals(TOTALS),
      )
    }

    const baseClientPrice = preferencias.descuento
      ? budgetTree?.clientPrice
      : budgetTree?.clientPriceWithoutDiscount

    return baseClientPrice ?? 0
  }, [
    budgetMode,
    preferencias.descuento,
    budgetTree?.clientPrice,
    budgetTree?.clientPriceWithoutDiscount,
    budgetTree?.priceReduced,
    marginModal.generalExpenses,
    marginModal.indirectExpenses,
    marginModal.industrialProfit,
    getDecimals,
    getUnitWithMargin,
    roundToDecimals,
  ])

  const formattedClientPriceTotal = useMemo(
    () =>
      currencyFormatDE(
        clientPriceTotalAmount,
        getCurrency(user?.["custom:currency"]),
        getDecimals(TOTALS),
      ),
    [clientPriceTotalAmount, user, getDecimals],
  )

  return { clientPriceTotalAmount, formattedClientPriceTotal }
}
